import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { View, SectionList, ScrollView, Keyboard } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import PropTypes from 'prop-types';
import NavigationService from 'NavigationService';
import { Background, ActivityIndicator, sectionListStyles, SectionListHeader } from 'LocalGluons';
import { t } from 'Locale';
import CancelableTextInput from 'LocalGluons/components/CancelableTextInput';
import favoritesInputProps from 'Pages/FavoriteSettings/utils/favoritesInputProps';
import { FavoriteListItem } from '../../components/FavoriteListItem';
import { FavoritePageWrapper } from '../FavoritePageWrapper';
import favoritesSettingsStyles from '../../FavoritesSettings.jss';
import { useCategories } from './useCategories';
import { ChannelFavoriteFilterList } from '../../components/ChannelFavoriteFilterList';
import { useFavoriteSettingsContext } from '../../FavoriteSettingsContext';

const ManageFavoritesMobile = ({ screenProps: { onSaveCallback, onCancelCallback } }) => {
    const { orderedFavoriteList, setOrderedFavoriteList, channelMap } =
        useFavoriteSettingsContext();
    const [configLoaded, categories] = useCategories(orderedFavoriteList);
    const [query, setQuery] = useState('');
    const [inputValue, setInputValue] = useState('');
    const navigation = useNavigation();

    // TODO: REFACTOR: DUPLICATE WITH CHANNELORDER.JS https://jira.24i.com/browse/PRJ1260EXT-3327
    const onChangeSearchTextCallback = useCallback((text) => {
        setQuery(text.toLowerCase());
        setInputValue(text);
    }, []);

    const getFavoriteItemProps = (item) => ({
        key: item.title,
        title: t(item.title),
        favorites: item.favoritesCount,
        onPress: () => {
            navigation.navigate('PackageFavorite', {
                activeCategory: item,
                onSaveCallback,
                onCancelCallback
            });
        }
    });

    useEffect(() => {
        // Update package favorites page
        const removeBlurListener = navigation.addListener('focus', () => {
            Keyboard.dismiss();
        });

        return () => {
            removeBlurListener();
        };
    }, []);

    const channelAvailability = useMemo(
        () =>
            categories.map((category) =>
                category.channelIds.map((chId) => channelMap[chId]).some(Boolean)
            ),
        [categories, channelMap]
    );

    return (
        <Background>
            {configLoaded && (
                <FavoritePageWrapper
                    onSaveCallback={onSaveCallback}
                    onCancelCallback={onCancelCallback}
                >
                    <View style={favoritesSettingsStyles.searchMarginContainer}>
                        <CancelableTextInput
                            {...{
                                ...favoritesInputProps,
                                containerStyle: favoritesSettingsStyles.searchInputContainer,
                                placeholder: t('SETTINGS.FAVOURITE_CHANNELS_SEARCH'),
                                onChangeText: onChangeSearchTextCallback,
                                value: inputValue,
                                displayCancelIcon: Boolean(query),
                                onCancel: (inputRef) => {
                                    setQuery('');
                                    inputRef.clear();
                                }
                            }}
                        />
                    </View>
                    {query ? (
                        <ChannelFavoriteFilterList
                            channelIds={Object.keys(channelMap)}
                            channelMap={channelMap}
                            orderedFavoriteList={orderedFavoriteList}
                            setOrderedFavoriteList={setOrderedFavoriteList}
                            query={query}
                        />
                    ) : (
                        <SectionList
                            style={sectionListStyles.container}
                            renderItem={({ item, index }) => (
                                <FavoriteListItem
                                    {...getFavoriteItemProps(item)}
                                    testID={`package_${index}`}
                                />
                            )}
                            renderSectionHeader={({ section: { title } }) => (
                                <View>
                                    <SectionListHeader title={t(title).toUpperCase()} />
                                </View>
                            )}
                            stickySectionHeadersEnabled={false}
                            sections={[
                                {
                                    title: 'COMMON.PACKAGE',
                                    data: categories.filter((_, i) => channelAvailability[i])
                                }
                            ]}
                            keyExtractor={(item, index) => item + index}
                        />
                    )}
                </FavoritePageWrapper>
            )}
        </Background>
    );
};

ManageFavoritesMobile.propTypes = {
    screenProps: PropTypes.shape({
        onSaveCallback: PropTypes.func,
        onCancelCallback: PropTypes.func
    }).isRequired
};

export { ManageFavoritesMobile };
